/**
 * Admin User Management Dashboard
 * Browse, search, and manage all user accounts
 */

import React, { useState, useEffect } from 'react';
import { Users, Search, Filter, Download, RefreshCw, Shield, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import {
    listUsers,
    getUserStats,
    exportUsers,
    formatDate,
    formatRelativeTime,
    getSubscriptionTierColor,
    getSubscriptionStatusColor,
    type UserListItem,
    type UserFilters,
    type PaginatedUsers,
} from '../services/userAdminService';
import Pagination from './Pagination';

interface AdminUserManagementProps {
    onBack?: () => void;
    onViewUser?: (uid: string) => void;
}

const AdminUserManagement: React.FC<AdminUserManagementProps> = ({ onBack, onViewUser }) => {
    // State
    const [users, setUsers] = useState<UserListItem[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [showFilters, setShowFilters] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalUsers, setTotalUsers] = useState(0);

    // Filters
    const [filters, setFilters] = useState<UserFilters>({
        sortBy: 'createdAt',
        sortOrder: 'desc',
    });

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1); // Reset to first page on new search
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Load users when filters or page changes
    useEffect(() => {
        loadUsers();
    }, [currentPage, debouncedSearch, filters]);

    // Load stats on mount
    useEffect(() => {
        loadStats();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        setError(null);

        try {
            const result: PaginatedUsers = await listUsers(currentPage, pageSize, {
                ...filters,
                searchQuery: debouncedSearch,
            });

            setUsers(result.users);
            setTotalUsers(result.total);
        } catch (err) {
            console.error('Error loading users:', err);
            setError(err instanceof Error ? err.message : 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const statsData = await getUserStats();
            setStats(statsData);
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    const handleExport = async () => {
        try {
            const blob = await exportUsers(filters);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting users:', err);
            setError('Failed to export users');
        }
    };

    const handleSelectUser = (uid: string) => {
        const newSelected = new Set(selectedUsers);
        if (newSelected.has(uid)) {
            newSelected.delete(uid);
        } else {
            newSelected.add(uid);
        }
        setSelectedUsers(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedUsers.size === users.length) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(users.map(u => u.uid)));
        }
    };

    const handleSort = (field: 'createdAt' | 'lastLoginAt' | 'email') => {
        setFilters(prev => ({
            ...prev,
            sortBy: field,
            sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
        }));
    };

    const getSortIcon = (field: string) => {
        if (filters.sortBy !== field) return null;
        return filters.sortOrder === 'asc' ?
            <ChevronUp className="w-4 h-4 inline" /> :
            <ChevronDown className="w-4 h-4 inline" />;
    };

    if (loading && users.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navigation Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    <span className="font-medium">Back</span>
                                </button>
                            )}
                            <nav className="flex items-center space-x-2 text-sm">
                                <span className="text-gray-500">Admin Portal</span>
                                <span className="text-gray-400">/</span>
                                <span className="text-gray-900 font-medium">User Management</span>
                            </nav>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={loadUsers}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                title="Refresh"
                            >
                                <RefreshCw className="w-4 h-4 inline mr-2" />
                                Refresh
                            </button>
                            <button
                                onClick={handleExport}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                                <Download className="w-4 h-4 inline mr-2" />
                                Export CSV
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                        <Users className="w-8 h-8 mr-3 text-indigo-600" />
                        User Management
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Browse, search, and manage all user accounts
                    </p>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                            <p className="text-sm text-gray-600 mb-1">Total Users</p>
                            <p className="text-3xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                            <p className="text-sm text-gray-600 mb-1">Active Users</p>
                            <p className="text-3xl font-bold text-green-600">{stats.activeUsers.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                            <p className="text-sm text-gray-600 mb-1">New This Month</p>
                            <p className="text-3xl font-bold text-blue-600">{stats.newUsersThisMonth.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                            <p className="text-sm text-gray-600 mb-1">Premium Users</p>
                            <p className="text-3xl font-bold text-purple-600">
                                {((stats.bySubscriptionTier?.pro || 0) + (stats.bySubscriptionTier?.executive || 0)).toLocaleString()}
                            </p>
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center space-x-4 mb-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by email or name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-3 rounded-lg font-medium transition-colors ${showFilters
                                ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                                : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            <Filter className="w-5 h-5 inline mr-2" />
                            Filters
                        </button>
                    </div>

                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subscription Tier
                                </label>
                                <select
                                    value={filters.subscriptionTier || ''}
                                    onChange={(e) => setFilters({ ...filters, subscriptionTier: e.target.value || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                                >
                                    <option value="">All Tiers</option>
                                    <option value="free">Free</option>
                                    <option value="starter">Starter</option>
                                    <option value="pro">Pro</option>
                                    <option value="executive">Executive</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subscription Status
                                </label>
                                <select
                                    value={filters.subscriptionStatus || ''}
                                    onChange={(e) => setFilters({ ...filters, subscriptionStatus: e.target.value || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="canceled">Canceled</option>
                                    <option value="past_due">Past Due</option>
                                    <option value="trialing">Trialing</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Account Status
                                </label>
                                <select
                                    value={filters.disabled === undefined ? '' : filters.disabled.toString()}
                                    onChange={(e) => setFilters({
                                        ...filters,
                                        disabled: e.target.value === '' ? undefined : e.target.value === 'true'
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                                >
                                    <option value="">All Accounts</option>
                                    <option value="false">Enabled</option>
                                    <option value="true">Disabled</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Users Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.size === users.length && users.length > 0}
                                            onChange={handleSelectAll}
                                            className="rounded border-gray-300"
                                        />
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('email')}
                                    >
                                        User {getSortIcon('email')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subscription
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('createdAt')}
                                    >
                                        Joined {getSortIcon('createdAt')}
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                                        onClick={() => handleSort('lastLoginAt')}
                                    >
                                        Last Login {getSortIcon('lastLoginAt')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                            <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                            <p>No users found</p>
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.uid} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.has(user.uid)}
                                                    onChange={() => handleSelectUser(user.uid)}
                                                    className="rounded border-gray-300"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {user.firstName && user.lastName
                                                                ? `${user.firstName} ${user.lastName}`
                                                                : user.displayName || 'Unnamed User'}
                                                        </div>
                                                        <div className="text-sm text-gray-500 flex items-center">
                                                            {user.email}
                                                            {user.emailVerified && (
                                                                <span title="Verified">
                                                                    <Shield className="w-3 h-3 ml-1 text-green-600" />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col space-y-1">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSubscriptionTierColor(user.subscriptionTier)}`}>
                                                        {user.subscriptionTier || 'Free'}
                                                    </span>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSubscriptionStatusColor(user.subscriptionStatus)}`}>
                                                        {user.subscriptionStatus || 'None'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {formatDate(user.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {formatRelativeTime(user.lastLoginAt || '')}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.disabled
                                                    ? 'bg-red-100 text-red-800 border border-red-300'
                                                    : 'bg-green-100 text-green-800 border border-green-300'
                                                    }`}>
                                                    {user.disabled ? 'Disabled' : 'Active'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right text-sm font-medium">
                                                <button
                                                    onClick={() => onViewUser?.(user.uid)}
                                                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4 inline" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {users.length > 0 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(totalUsers / pageSize)}
                                totalItems={totalUsers}
                                itemsPerPage={pageSize}
                                onPageChange={setCurrentPage}
                            />
                            <p className="text-sm text-gray-600 text-center mt-2">
                                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers.toLocaleString()} users
                            </p>
                        </div>
                    )}
                </div>

                {/* Bulk Actions */}
                {selectedUsers.size > 0 && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-lg border-2 border-indigo-600 p-4">
                        <div className="flex items-center space-x-4">
                            <span className="text-sm font-medium text-gray-700">
                                {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                            </span>
                            <button
                                onClick={() => setSelectedUsers(new Set())}
                                className="text-sm text-gray-600 hover:text-gray-900"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUserManagement;
